import type { MetagameTokenPackageResponse } from '../dto/metagames.types';
import { MetaGamesEventType } from '../dto/metagames.types';

import { getRequest } from './api';

/**
 * Service for fetching metagame token package pricing information
 */
export class MetagameTokenService {
  /**
   * Fetches the price and details for purchasing metagame tokens
   * @param quantity - The number of tokens to purchase
   * @param eventType - The type of metagame event (hammers, prizeDrop, puzzle, trail, bingo)
   * @returns Promise with package details including price, currency, and bundle information
   */
  static async getTokenPackagePrice(
    quantity: number,
    eventType: MetaGamesEventType,
  ): Promise<MetagameTokenPackageResponse> {
    const params = {
      quantity,
      eventType,
    } as Record<string, unknown>;

    return getRequest<MetagameTokenPackageResponse>('metagame-events/token-package', params);
  }

  /**
   * Convenience method to get package price for hammer tokens
   * @param quantity - The number of hammer tokens to purchase
   */
  static async getHammerTokenPackagePrice(quantity: number): Promise<MetagameTokenPackageResponse> {
    return this.getTokenPackagePrice(quantity, MetaGamesEventType.HAMMERS);
  }

  /**
   * Convenience method to get package price for prize drop tokens
   * @param quantity - The number of prize drop tokens to purchase
   */
  static async getPrizeDropTokenPackagePrice(quantity: number): Promise<MetagameTokenPackageResponse> {
    return this.getTokenPackagePrice(quantity, MetaGamesEventType.PRIZE_DROP);
  }

  /**
   * Convenience method to get package price for puzzle tokens
   * @param quantity - The number of puzzle tokens to purchase
   */
  static async getPuzzleTokenPackagePrice(quantity: number): Promise<MetagameTokenPackageResponse> {
    return this.getTokenPackagePrice(quantity, MetaGamesEventType.PUZZLE);
  }

  /**
   * Convenience method to get package price for trail tokens
   * @param quantity - The number of trail tokens to purchase
   */
  static async getTrailTokenPackagePrice(quantity: number): Promise<MetagameTokenPackageResponse> {
    return this.getTokenPackagePrice(quantity, MetaGamesEventType.TRAIL);
  }

  /**
   * Convenience method to get package price for bingo tokens
   * @param quantity - The number of bingo tokens to purchase
   */
  static async getBingoTokenPackagePrice(quantity: number): Promise<MetagameTokenPackageResponse> {
    return this.getTokenPackagePrice(quantity, MetaGamesEventType.BINGO);
  }
}
